import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import "./AdminVolatility.css";

const NUMERIC_FIELDS = new Set([
  "stake", "payout", "profit", "result", "entryPrice", "entryDigit",
  "entryTick", "entryTickId", "exitPrice", "exitDigit", "exitTick",
  "exitTickId", "barrier", "prediction", "balanceBefore", "balanceAfter",
  "entryPriceDecimals", "duration", "durationTicks", "contractDuration",
]);

const BOOLEAN_FIELDS = new Set(["isDemo", "isTest", "settled"]);
const HIDDEN_EDIT_FIELDS = new Set([
  "id", "createdAt", "updatedAt", "closedAt",
]);

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }

  if (value instanceof Date) return value.toLocaleString();

  if (typeof value === "object") return JSON.stringify(value);

  return String(value);
}

function valueForEditor(value) {
  if (value === null || value === undefined) return "";

  if (typeof value?.toDate === "function") {
    return value.toDate().toISOString();
  }

  if (value instanceof Date) return value.toISOString();

  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

function convertEditedValue(key, text, original) {
  if (text === "" && (original === null || original === undefined)) {
    return null;
  }

  if (NUMERIC_FIELDS.has(key) || typeof original === "number") {
    if (text.trim() === "") return null;

    const n = Number(text);

    if (!Number.isFinite(n)) {
      throw new Error(`${key}: weka namba sahihi.`);
    }

    return n;
  }

  if (BOOLEAN_FIELDS.has(key) || typeof original === "boolean") {
    if (text === "true") return true;
    if (text === "false") return false;

    throw new Error(`${key}: chagua true au false.`);
  }

  if (
    original &&
    typeof original === "object" &&
    typeof original.toDate !== "function" &&
    !(original instanceof Date)
  ) {
    if (text.trim() === "") return {};

    try {
      return JSON.parse(text);
    } catch {
      throw new Error(`${key}: JSON si sahihi.`);
    }
  }

  return text;
}

const TABLE_FIELDS = [
  ["username", "Username"],
  ["userId", "User ID"],
  ["status", "Status"],
  ["orderType", "Order type"],
  ["tradeType", "Trade type"],
  ["contractType", "Contract"],
  ["marketName", "Market"],
  ["marketSymbol", "Symbol"],
  ["stake", "Stake"],
  ["payout", "Payout"],
  ["profit", "Profit"],
  ["result", "Result"],
  ["entryPrice", "Entry price"],
  ["entryDigit", "Entry digit"],
  ["entryTick", "Entry tick"],
  ["exitPrice", "Exit price"],
  ["exitDigit", "Exit digit"],
  ["exitTick", "Exit tick"],
  ["barrier", "Barrier"],
  ["prediction", "Prediction"],
  ["currency", "Currency"],
  ["tradingMode", "Trading mode"],
  ["balanceBefore", "Balance before"],
  ["balanceAfter", "Balance after"],
  ["createdAt", "Created"],
  ["updatedAt", "Updated"],
  ["closedAt", "Closed"],
];

export default function AdminVolatility() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "volatilityorder"),
      (snap) => {
        const rows = snap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        rows.sort(
          (a, b) =>
            timestampValue(b.closedAt || b.updatedAt || b.createdAt) -
            timestampValue(a.closedAt || a.updatedAt || a.createdAt)
        );

        setOrders(rows);
        setLoading(false);
        setLoadError("");
      },
      (error) => {
        console.error("AdminVolatility load error:", error);
        setLoadError(
          "Imeshindikana kusoma rekodi. Hakikisha admin ana ruhusa za Firestore."
        );
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return orders.filter((o) => {
      const status = String(o.status || "").toUpperCase();

      const tabMatch =
        tab === "ALL" ||
        (tab === "OPEN" && status === "OPEN") ||
        (
          tab === "CLOSED" &&
          ["WON", "LOST", "CLOSED", "COMPLETED"].includes(status)
        );

      const searchMatch =
        !term ||
        [
          o.username,
          o.userId,
          o.marketName,
          o.marketSymbol,
          o.id,
          o.localTradeId,
          o.tradeType,
          o.contractType,
          o.status,
        ].some((v) =>
          String(v ?? "").toLowerCase().includes(term)
        );

      return tabMatch && searchMatch;
    });
  }, [orders, search, tab]);

  const stats = useMemo(
    () => ({
      all: orders.length,
      open: orders.filter(
        (o) => String(o.status).toUpperCase() === "OPEN"
      ).length,
      closed: orders.filter((o) =>
        ["WON", "LOST", "CLOSED", "COMPLETED"].includes(
          String(o.status).toUpperCase()
        )
      ).length,
    }),
    [orders]
  );

  function startEdit(order) {
    setEditing(order);

    const initial = {};

    Object.entries(order).forEach(([key, value]) => {
      if (key !== "id") {
        initial[key] = valueForEditor(value);
      }
    });

    setDraft(initial);
    setMessage("");
  }

  async function saveEdit(event) {
    event.preventDefault();

    if (!editing) return;

    setSaving(true);
    setMessage("");

    try {
      const changes = {};

      Object.entries(draft).forEach(([key, text]) => {
        if (HIDDEN_EDIT_FIELDS.has(key)) return;

        changes[key] = convertEditedValue(
          key,
          text,
          editing[key]
        );
      });

      changes.updatedAt = serverTimestamp();

      await updateDoc(
        doc(db, "volatilityorder", editing.id),
        changes
      );

      setEditing(null);
      setMessage("Mabadiliko yamehifadhiwa.");
    } catch (error) {
      console.error("Save volatility order error:", error);
      setMessage(
        error.message || "Imeshindikana kuhifadhi mabadiliko."
      );
    } finally {
      setSaving(false);
    }
  }

  async function settleOrder(order, won) {
    if (!order || String(order.status || "").toUpperCase() !== "OPEN") {
      setMessage("Trade hii haiko OPEN; haiwezi kufungwa tena.");
      return;
    }

    const outcome = won ? "WON" : "LOST";
    const stake = Number(order.stake) || 0;
    const payout = won ? Number((stake * 1.98).toFixed(2)) : 0;
    const profit = won
      ? Number((stake * 0.98).toFixed(2))
      : Number((-stake).toFixed(2));

    const confirmText = won
      ? `Thibitisha WIN kwa ${order.username || "user"}? Payout ${payout.toFixed(2)} itaongezwa kwenye balance.`
      : `Thibitisha LOST kwa ${order.username || "user"}? Stake ilishakatwa wakati trade ilipofunguliwa.`;

    if (!window.confirm(confirmText)) return;

    setMessage("");
    try {
      // Tafuta akaunti ya mtumiaji kwa username; hakuna balance inayobadilishwa
      // kama akaunti haijapatikana.
      const usersQuery = query(
        collection(db, "users"),
        where("username", "==", String(order.username || ""))
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        throw new Error("Akaunti ya user haijapatikana; hakuna kilichobadilishwa.");
      }
      if (usersSnapshot.docs.length > 1) {
        throw new Error("Username hii ina akaunti zaidi ya moja; rekebisha duplicate kwanza.");
      }

      const userRef = doc(db, "users", usersSnapshot.docs[0].id);
      const orderRef = doc(db, "volatilityorder", order.id);
      let alreadyClosed = false;

      await runTransaction(db, async (transaction) => {
        const orderSnapshot = await transaction.get(orderRef);
        if (!orderSnapshot.exists()) throw new Error("Trade haijapatikana.");

        const orderData = orderSnapshot.data();
        if (String(orderData.status || "").toUpperCase() !== "OPEN") {
          alreadyClosed = true;
          return;
        }

        const userSnapshot = await transaction.get(userRef);
        if (!userSnapshot.exists()) throw new Error("Akaunti ya user haijapatikana.");

        const userData = userSnapshot.data();
        const balanceBefore = Number(userData.usdtBalanceOther) || 0;
        // Kama ilivyo Volatility: stake hukatwa wakati wa kufungua;
        // WIN huongeza payout (stake + profit), LOST haiongezi pesa.
        const balanceAfter = won
          ? Number((balanceBefore + payout).toFixed(2))
          : Number(balanceBefore.toFixed(2));

        if (won) {
          transaction.update(userRef, { usdtBalanceOther: balanceAfter });
        }

        transaction.update(orderRef, {
          status: outcome,
          result: profit,
          profit,
          payout,
          balanceBeforeClose: balanceBefore,
          balanceAfterClose: balanceAfter,
          settled: true,
          closedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          adminSettled: true,
        });
      });

      if (alreadyClosed) {
        setMessage("Trade ilikuwa tayari imefungwa; hakuna balance iliyobadilishwa.");
        return;
      }
      setMessage(`Trade imewekwa ${outcome}${won ? `; payout ${payout.toFixed(2)} imeongezwa.` : "."}`);
    } catch (error) {
      console.error("Admin settle volatility order error:", error);
      setMessage(error.message || "Imeshindikana kufunga trade.");
    }
  }

  async function removeOrder(order) {
    const confirmText =
      `Una uhakika unataka kufuta trade ya ${
        order.username || "username haijulikani"
      }? Kitendo hiki hakiwezi kurudishwa.`;

    if (!window.confirm(confirmText)) return;

    try {
      await deleteDoc(
        doc(db, "volatilityorder", order.id)
      );

      setMessage("Rekodi imefutwa.");

      if (editing?.id === order.id) {
        setEditing(null);
      }
    } catch (error) {
      console.error("Delete volatility order error:", error);
      setMessage(
        "Imeshindikana kufuta rekodi. Kagua ruhusa za Firestore."
      );
    }
  }

  return (
    <div className="admin-ant-container admin-volatility-container">
      <h2>ADMIN VOLATILITY — RECORDS & TRANSACTIONS</h2>

      <div className="av-summary">
        <div className="summary-card">
          <h3>ALL RECORDS</h3>
          <p>{stats.all}</p>
        </div>

        <div className="summary-card">
          <h3>OPEN</h3>
          <p>{stats.open}</p>
        </div>

        <div className="summary-card">
          <h3>CLOSED</h3>
          <p>{stats.closed}</p>
        </div>
      </div>

      <div className="av-toolbar">
        <input
          className="av-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search username, user ID, market, trade ID..."
          aria-label="Search volatility records"
        />

        <div className="av-tabs">
          {[
            ["ALL", "All transactions"],
            ["OPEN", "Open"],
            ["CLOSED", "Closed"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={
                tab === value ? "av-tab active" : "av-tab"
              }
              onClick={() => setTab(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className="admin-message av-message">
          {message}
        </div>
      )}

      {loadError && (
        <div className="av-error">{loadError}</div>
      )}

      <div className="av-result-count">
        Showing {filtered.length} of {orders.length} records
      </div>

      <div className="table-wrapper av-table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Firestore ID</th>

              {TABLE_FIELDS.map(([key, label]) => (
                <th key={key}>{label}</th>
              ))}

              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_FIELDS.length + 2}>
                  Loading records...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={TABLE_FIELDS.length + 2}>
                  Hakuna rekodi zilizopatikana.
                </td>
              </tr>
            ) : (
              filtered.map((order) => (
                <tr key={order.id}>
                  <td title={order.id}>{order.id}</td>

                  {TABLE_FIELDS.map(([key]) => (
                    <td key={key}>
                      {displayValue(order[key])}
                    </td>
                  ))}

                  <td>
                    <div className="av-actions">
                      <button
                        className="av-edit-btn"
                        onClick={() => startEdit(order)}
                      >
                        EDIT
                      </button>

                      {String(order.status || "").toUpperCase() === "OPEN" && (
                        <>
                          <button
                            type="button"
                            className="av-win-btn"
                            onClick={() => settleOrder(order, true)}
                          >
                            WIN
                          </button>
                          <button
                            type="button"
                            className="av-lost-btn"
                            onClick={() => settleOrder(order, false)}
                          >
                            LOST
                          </button>
                        </>
                      )}
                      <button
                        className="delete-btn"
                        onClick={() => removeOrder(order)}
                      >
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="av-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (
              e.target === e.currentTarget &&
              !saving
            ) {
              setEditing(null);
            }
          }}
        >
          <section
            className="av-editor"
            role="dialog"
            aria-modal="true"
            aria-label="Edit volatility record"
          >
            <div className="av-editor-head">
              <h3>Edit Volatility Record</h3>

              <button
                type="button"
                className="av-close-btn"
                onClick={() => setEditing(null)}
                disabled={saving}
              >
                CLOSE
              </button>
            </div>

            <p className="av-record-id">
              Firestore ID: {editing.id}
            </p>

            <p className="av-warning">
              Unahariri document ya trade pekee.
              Kubadilisha kiasi hapa hakubadilishi balance
              ya mtumiaji moja kwa moja. Timestamp za
              createdAt/updatedAt/closedAt zinasimamiwa
              na mfumo.
            </p>

            <form onSubmit={saveEdit}>
              <div className="av-fields">
                {Object.entries(draft)
                  .filter(
                    ([key]) => !HIDDEN_EDIT_FIELDS.has(key)
                  )
                  .map(([key, value]) => {
                    const isLong =
                      value.length > 90 ||
                      (
                        editing[key] &&
                        typeof editing[key] === "object" &&
                        typeof editing[key].toDate !== "function" &&
                        !(editing[key] instanceof Date)
                      );

                    return (
                      <label
                        className={
                          isLong
                            ? "av-field av-field-wide"
                            : "av-field"
                        }
                        key={key}
                      >
                        <span>{key}</span>

                        {isLong ? (
                          <textarea
                            value={value}
                            onChange={(e) =>
                              setDraft((old) => ({
                                ...old,
                                [key]: e.target.value,
                              }))
                            }
                            rows={4}
                          />
                        ) : (
                          <input
                            value={value}
                            type={
                              NUMERIC_FIELDS.has(key) ||
                              typeof editing[key] === "number"
                                ? "number"
                                : "text"
                            }
                            step="any"
                            onChange={(e) =>
                              setDraft((old) => ({
                                ...old,
                                [key]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </label>
                    );
                  })}
              </div>

              <div className="av-editor-actions">
                <button
                  type="submit"
                  className="av-save-btn"
                  disabled={saving}
                >
                  {saving ? "SAVING..." : "SAVE CHANGES"}
                </button>

                <button
                  type="button"
                  className="av-cancel-btn"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                >
                  CANCEL
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
